/**
 * DeviceSignIn — one of ofSelf's own programs asking to sign in as you on one device:
 * dev.oS and the paradigm CLI (/connect-builder), Nucleus on a Mac (/connect-mac),
 * Nucleus on an iPhone (/connect-phone).
 *
 * DRAWN AS AN APP SIGNING IN, because to the person it is one. Every consent page is the
 * grant page's frame (pages/grant/consentFrame.test.js holds that): the bond between you
 * and the program, a headline saying what it asks, what it can and cannot do, which
 * device and when it ends, then Deny or Sign in at the bottom of the window.
 *
 * The program's route keeps what differs: its audience, where the code may go, and its
 * words. `approve` gets the code and sends it, and throws to say why it could not;
 * `deny` sends the refusal.
 *
 * THE WAY BACK (`ended`, from `?done=`) is drawn here too. A program that waits for the
 * code to be exchanged before answering the browser sends it back with how it ended, so
 * the last page is ofSelf's and says "signed in" only once it is.
 */
import { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { GrantFrame } from './GrantPage';
import { Bond, Decide, Explanation, Section } from './parts';
import '../../pages/grant/GrantRoute.css';

const ENDED = {
  'signed-in': 'is signed in.',
  cancelled: 'was not signed in.',
  failed: 'could not sign in.',
};

const name = (label, handle) => (
  <span className={`gp-bond__name${handle ? ' is-handle' : ''}`}>{label}</span>
);

function Lines({ lines }) {
  return (
    <ul className="gp-wants">
      {lines.map((l) => (
        <li key={l} className="gp-want"><span className="gp-want__mark" /><span className="gp-want__t">{l}</span></li>
      ))}
    </ul>
  );
}

export default function DeviceSignIn({
  app,              // the program, as the bond and headline name it
  where,            // 'this computer' | 'this Mac' | 'this iPhone'
  device = '',      // the name the program gave the device, unchecked
  asked,            // what the person did a moment ago, if this is theirs
  retry,            // how to start again
  can = [], cannot = [],
  valid,            // the link carries what the program sends
  ended,            // 'signed-in' | 'cancelled' | 'failed', on the way back
  sent = null,      // what to say once the code has gone and nothing comes back here
  approve, deny,
  testId,
}) {
  const { user } = useAuth();
  const [state, setState] = useState('ask');   // ask | sending | sent | error
  const [error, setError] = useState(null);

  const bond = (
    <Bond state={ended && ended !== 'signed-in' ? 'none' : 'full'} names
          you={user?.username ? name(user.username, true) : name('You', false)}
          app={() => name(app, false)} />
  );

  if (ENDED[ended]) {
    return (
      <GrantFrame className="gp-route" data-testid={testId}>
        {bond}
        <Explanation headline={<><b>{app}</b> {ENDED[ended]}</>}
                     sub={ended === 'failed' ? `${retry} to try again.` : 'You can close this tab.'} />
      </GrantFrame>
    );
  }

  if (!valid) {
    return (
      <GrantFrame className="gp-route">
        <Explanation headline={`This link didn’t come from ${app}.`} sub={`${retry}.`} />
      </GrantFrame>
    );
  }

  if (state === 'sent') {
    return sent ? (
      <GrantFrame className="gp-route" data-testid={testId}>
        {bond}
        <Explanation headline={sent} />
      </GrantFrame>
    ) : <GrantFrame className="gp-route" data-testid="handing-back" aria-busy="true" />;
  }

  const onApprove = async () => {
    setState('sending');
    try {
      await approve();
      setState('sent');
    } catch (e) {
      setError(e?.message || `Could not sign ${app} in.`);
      setState('error');
    }
  };

  const onDeny = () => {
    deny();
    setState('sent');
  };

  return (
    <GrantFrame className="gp-route" data-testid={testId}>
      {bond}
      <Explanation headline={<><b>{app}</b> is asking to sign in as you on {where}.</>}
                   sub={`Continue only if you just ${asked}.`} />
      {can.length > 0 && <Section title={`What ${app} can do`}><Lines lines={can} /></Section>}
      {cannot.length > 0 && <Section title="What it cannot do"><Lines lines={cannot} /></Section>}
      <div className="gp-terms-row">
        <div className="gp-term" role="note">
          <span className="gp-term__k">On</span>
          <span className="gp-term__v">{device || where.replace(/^this/, 'This')}</span>
        </div>
        <div className="gp-term" role="note">
          <span className="gp-term__k">Ends</span>
          <span className="gp-term__v">When you sign it out or stop using it</span>
        </div>
      </div>
      {error && <p className="gp-route__err" role="alert">{error}</p>}
      <Decide confirmLabel={state === 'sending' ? 'Signing in…' : 'Sign in'} denyLabel="Deny"
              disabled={state === 'sending'} onConfirm={onApprove} onDeny={onDeny} />
    </GrantFrame>
  );
}
